import React, { useState, useMemo, useEffect } from 'react';
import { useTable, usePagination, useSortBy, useResizeColumns, useFlexLayout } from 'react-table';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { reducer, useEffectToDispatch, fetchApiData, initialState, Sorting } from '@/shared/apiTableHelper';
import { REDEMPTION_RISK_EVENT_COLUMNS } from './columns';
import { CLASSIFICATION_OPTIONS, STATUS_OPTIONS } from './helpers';

const queryClient = new QueryClient();

// The detail page is outside this table's QueryClientProvider; it calls this
// after approve/decline/mark safe so the queue doesn't show the old status.
export const invalidateRedemptionRiskEvents = () => queryClient.invalidateQueries('redemption-risk-events');

const DataTable = ({ organization }) => {
    const [{ queryPageIndex, queryPageSize, totalCount, queryPageSortBy }, dispatch] = React.useReducer(reducer, initialState);
    const [classification, setClassification] = useState('');
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [keyword, setKeyword] = useState('');

    // Search by user email/name or merchant; wait for typing to pause
    // before querying.
    useEffect(() => {
        const timer = setTimeout(() => setKeyword(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Two plain <select>s feeding the query key directly, rather than
    // apiTableHelper's filter/useFilter path - that resets to page 0 on
    // every render (not just on filter change) when handed a freshly
    // constructed filter object each render, which would fight the
    // classification/status selects below.
    const columns = useMemo(() => REDEMPTION_RISK_EVENT_COLUMNS, []);

    const { isLoading, error, data, isSuccess } = useQuery(
        ['redemption-risk-events', organization.id, queryPageIndex, queryPageSize, queryPageSortBy, classification, status, keyword],
        () => fetchApiData({
            url: `/organization/${organization.id}/redemption-risk-event`,
            page: queryPageIndex,
            size: queryPageSize,
            sortby: queryPageSortBy,
            filter: {
                ...(classification ? { classification } : {}),
                ...(status ? { status } : {}),
                ...(keyword ? { keyword } : {}),
            },
        }),
        {
            keepPreviousData: true,
            staleTime: Infinity,
        }
    );

    const totalPageCount = Math.ceil(totalCount / queryPageSize);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        pageOptions,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy },
    } = useTable({
        columns,
        data: data ? data.results : [],
        initialState: {
            pageIndex: queryPageIndex,
            pageSize: queryPageSize,
            sortBy: queryPageSortBy,
        },
        manualPagination: true,
        manualSortBy: true, // backend does the sorting (see ALLOWED_SORTS in RedemptionReviewService) - without this, useSortBy also re-sorts the current page's rows in memory, which briefly shows a different order than what the refetch then returns for columns the backend can't sort by.
        pageCount: data ? totalPageCount : null,
        autoResetSortBy: false,
        autoResetPage: false,
    },
        useSortBy,
        usePagination,
        useResizeColumns,
        useFlexLayout,
    );

    useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, data });

    // A new search starts from the first page.
    useEffect(() => {
        gotoPage(0);
    }, [keyword]); // eslint-disable-line react-hooks/exhaustive-deps

    if (error) {
        return <p>Error: {JSON.stringify(error)}</p>;
    }

    return (
        <>
            <div className="row mb-3">
                <div className="col-md-4">
                    <input
                        type="search"
                        className="form-control"
                        placeholder="Search user or merchant"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </div>
                <div className="col-md-3">
                    <select
                        className="form-control"
                        value={classification}
                        onChange={(e) => { setClassification(e.target.value); gotoPage(0); }}
                    >
                        {CLASSIFICATION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-3">
                    <select
                        className="form-control"
                        value={status}
                        onChange={(e) => { setStatus(e.target.value); gotoPage(0); }}
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading && <p>Loading...</p>}

            {isSuccess && (
                <>
                    <div className="table react-table" style={{ overflowX: 'auto' }}>
                        <table {...getTableProps()} className="table">
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map(column => (
                                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                                {column.render('Header')}
                                                {column.isSorted ? <Sorting column={column} /> : ''}
                                                <div
                                                    {...column.getResizerProps()}
                                                    className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                                                />
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="table table--bordered" {...getTableBodyProps()}>
                                {page.length === 0 && (
                                    <tr><td colSpan={columns.length}>No redemption risk events found.</td></tr>
                                )}
                                {page.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map(cell => (
                                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {(rows.length > 0) && (
                        <ReactTablePagination
                            page={page}
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageSize={pageSize}
                            pageIndex={pageIndex}
                            pageCount={pageCount}
                            setPageSize={setPageSize}
                            manualPageSize={[]}
                            dataLength={totalCount}
                        />
                    )}
                </>
            )}
        </>
    );
};

const TableWrapper = ({ organization }) => {
    if (!organization) return 'Loading...';
    return (
        <QueryClientProvider client={queryClient}>
            <DataTable organization={organization} />
        </QueryClientProvider>
    );
};

export default withRouter(connect((state) => ({
    organization: state.organization,
}))(TableWrapper));
